import { Badge } from '@vrplatform/components';
import { useDashboard, useEnabledFeatures } from '@vrplatform/data';
import { Icon } from '@vrplatform/icons';
import type { MRT_ColumnDef } from '@vrplatform/table';
import {
  type Maybe,
  ensure,
  formatCurrency,
  hasValue,
  utc,
} from '@vrplatform/utils';
import {
  Box,
  Group,
  Text,
  useMantineColorScheme,
  useMantineTheme,
} from '@mantine/core';
import { useMemo } from 'react';
import {
  PaymentsReservationsActionIcons,
  ReservationStatusBadge,
} from '../../../components';
import type { ReservationRow } from './useReservationsTableQuery';

type Props = {
  hideTotal: boolean;
};

export const useReservationsTableColumns = ({ hideTotal }: Props) => {
  const [dashboard] = useDashboard();
  const { GL_WITH_LEGACY_AUTOMATIONS } = useEnabledFeatures();
  const isOwnerPortal = dashboard === 'owner';

  return useMemo(
    () =>
      ensure<(MRT_ColumnDef<ReservationRow> | undefined)[]>([
        {
          header: 'Reservation / Confirmation Code',
          maxSize: 100,
          Cell: ({ row }) => {
            const data = row.original;
            return (
              <TextCell
                firstLine={data.guestName}
                secondLine={
                  data.confirmationCode?.replace(/(.{22})..+/, '$1...') || '-'
                }
              />
            );
          },
        },
        // {
        //   accessorKey: 'connection',
        //   enableSorting: false,
        //   header: 'Connection',
        //   maxSize: 120,
        //   Cell: ({ row }) => {
        //     const data = row.original;

        //     const connectionName =
        //       data.connection?.app?.id === 'finalytic'
        //         ? appName
        //         : data.connection?.name;

        //     return (
        //       <TextCell
        //         firstLine={connectionName}
        //         secondLine={data.listing?.name}
        //         icon={
        //           data.connection?.app?.icon && (
        //             <Avatar
        //               src={data.connection.app?.icon}
        //               size="sm"
        //               sx={(theme) => ({
        //                 border: `1px solid ${theme.colors.gray[2]}`,
        //               })}
        //             />
        //           )
        //         }
        //       />
        //     );
        //   },
        // },
        {
          accessorKey: 'checkIn',
          header: 'Dates',
          maxSize: 100,
          enableSorting: true,
          Cell: ({ row }) => {
            const data = row.original;

            if (!data) return null;

            const line1 = `${utc(data?.checkIn).format('DD. MMM')} -
          ${utc(data?.checkOut).format('DD. MMM. YYYY')}`;
            const line2 = `${data?.nights || 0} nights${
              data?.bookedAt
                ? `, on ${utc(data?.bookedAt).format('DD. MMM')}`
                : ''
            }`;

            return <TextCell firstLine={line1} secondLine={line2} />;
          },
        },

        {
          header: 'Listing / Booking Channel',
          maxSize: 100,
          Cell: ({ row }) => {
            const data = row.original;
            return (
              <TextCell
                firstLine={data.listing?.name || '-'}
                secondLine={data.bookingPlatform || '-'}
              />
            );
          },
        },

        isOwnerPortal || !GL_WITH_LEGACY_AUTOMATIONS
          ? undefined
          : {
              header: 'Automations',
              accessorKey: 'sourceId',
              maxSize: 70,
              Cell: ({ row }) => {
                const data = row.original;

                if (!data.id) return null;

                return (
                  <PaymentsReservationsActionIcons
                    id={data.id}
                    type="reservation"
                  />
                );
              },
            },

        {
          accessorKey: 'status',
          header: 'Status',
          size: 200,
          minSize: 200,
          maxSize: 0,
          mantineTableBodyCellProps: { align: 'center' },
          mantineTableHeadCellProps: {
            align: 'center',
            px: 0,
            sx: (theme) => ({
              '.mrt-table-head-cell-labels': {
                paddingLeft: 0,
                fontWeight: 400,
                color: theme.colors.gray[7],
              },
            }),
          },
          Cell: ({ row }) => {
            const data = row.original;

            if (
              row.original.issues?.find(
                (x) => x.code === 'unassignedAccount'
              ) &&
              !isOwnerPortal
            )
              return (
                <Badge
                  tooltip="Reservation includes unmapped financial lines"
                  color="red"
                  leftIcon={
                    <Icon
                      icon="AlertTriangleIcon"
                      size={16}
                      color={(theme) => theme.colors.red[6]}
                    />
                  }
                >
                  Unmapped financials
                </Badge>
              );

            return (
              <ReservationStatusBadge
                status={data.status}
                cancelledAt={data.cancelledAt}
                generalLedgerStatus={data.generalLedgerStatus}
              />
            );
          },
        },
        hideTotal
          ? undefined
          : {
              accessorKey: 'total',
              maxSize: 50,
              header: 'Total',
              mantineTableHeadCellProps: {
                align: 'right',
              },
              mantineTableBodyCellProps: {
                align: 'right',
              },
              Cell: ({ row }) => {
                const data = row.original;
                const [dashboard] = useDashboard();
                const isOwnerPortal = dashboard === 'owner';
                const currency = data.currency || undefined;

                const payment = data.payment || {
                  expected: 0,
                  received: 0,
                  status: 'paid',
                };

                const line1 = useMemo(
                  () => formatCurrency(payment.expected / 100, currency),
                  [payment.expected, currency]
                );

                const line2 = useMemo(() => {
                  if (isOwnerPortal) return undefined;
                  if (payment.status === 'coHost') return undefined;

                  const paid = payment.received;
                  const unpaid = payment.expected - payment.received;

                  if (data.status === 'canceled') {
                    return paid && paid !== payment.expected
                      ? `${formatCurrency(paid / 100, currency)} paid`
                      : undefined;
                  }

                  return unpaid
                    ? `${formatCurrency(unpaid / 100, currency)} unpaid`
                    : undefined;
                }, [
                  currency,
                  data.status,
                  payment.received,
                  payment.expected,
                  payment.status,
                  isOwnerPortal,
                ]);

                return <TextCell firstLine={line1} secondLine={line2} />;
              },
            },
      ]).filter(hasValue),
    [isOwnerPortal, GL_WITH_LEGACY_AUTOMATIONS, hideTotal]
  );
};

const TextCell = ({
  firstLine,
  secondLine,
  icon,
}: {
  firstLine: Maybe<string>;
  secondLine: Maybe<string>;
  icon?: React.ReactNode;
}) => {
  const { colors } = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();

  return (
    <Group wrap="nowrap" gap="sm">
      {icon}
      <Box>
        {firstLine && (
          <Text size="sm" component="p" m={0}>
            {firstLine}
          </Text>
        )}
        {secondLine && (
          <Text
            color={colorScheme === 'dark' ? colors.gray[6] : 'gray'}
            size="xs"
            component="p"
            m={0}
          >
            {secondLine}
          </Text>
        )}
      </Box>
    </Group>
  );
};
